import React from 'react';

import { useIntl } from 'react-intl-next';

import { Box, Inline, Text } from '@atlaskit/primitives/compiled';
import { AgentAvatar } from '@atlaskit/rovo-agent-components';
import { usePublish } from '@atlaskit/rovo-triggers';
import { AgentCreatedBy } from '@atlassian/conversation-assistant-agent/utils/agents';
import { type Agent } from '@atlassian/conversation-assistant-service';
import { FollowUpButton } from '@atlassian/conversation-assistant-ui-components';

import { useAIAnswerContext } from '../../ai-answer-dialog/ai-answer-context';
import { messages } from '../../messages';
import { KnowledgeCardHeader } from '../common/knowledge-card-header';
import { getAgentProfileUrl } from '../util';

type AgentCardHeaderProps = {
	agent: Agent;
	imageUrl?: string;
};

export const AgentCardHeader = ({ agent }: AgentCardHeaderProps): React.JSX.Element => {
	const { commonAttributes } = useAIAnswerContext();
	const { cloudId, orgId } = commonAttributes;
	const { formatMessage } = useIntl();

	const publish = usePublish('ai-mate');

	const media = (
		<Box>
			<AgentAvatar
				agentId={agent.id}
				agentNamedId={agent.id}
				agentIdentityAccountId={agent.identity_account_id}
				size="xlarge"
			/>
		</Box>
	);

	const additionalInfo = (
		<>
			<Text color="color.text.subtlest" maxLines={2}>
				{agent.description}
			</Text>
			<Inline alignBlock="center">
				<Inline alignBlock="center" space="space.025">
					<Text color="color.text.subtlest">
						{formatMessage(messages.knowledge_cards_rovo_agent_by)}
					</Text>
					<AgentCreatedBy agent={agent} siteId={cloudId} simplified />
				</Inline>
			</Inline>
		</>
	);

	return (
		<KnowledgeCardHeader
			name={agent.name}
			profileUrl={getAgentProfileUrl(agent, orgId)}
			profileLinkText={formatMessage(messages.knowledge_cards_view_agent)}
			media={media}
			additionalInfo={additionalInfo}
			testId="agent-card-header"
			headerActions={
				<Inline alignBlock="center">
					<FollowUpButton
						followUpIndex={0}
						followUpType="agent"
						followUpText="Chat to agent"
						experienceId="sain"
						onClick={() => {
							publish({
								type: 'chat-new',
								source: 'sain',
								data: {
									agentId: agent.id,
									dialogues: [],
									sourceId: commonAttributes.searchSessionId,
									name: `Chat with ${agent.name}`,
								},
							});
						}}
					/>
				</Inline>
			}
		/>
	);
};
